import React, { useState } from 'react';

const validateUrl = (url) => {
  const trimmed = url.trim();
  if (trimmed !== url) return 'URL should not contain leading or trailing spaces';
  if (url.includes(' ')) return 'URL should not contain spaces';

  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return 'URL must start with http:// or https://';
    }
  } catch (e) {
    return 'Please enter a valid URL';
  }
  return '';
};

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;

const ShortLinkField = ({ value = {}, title, onChange, label = 'Destination URL', hint }) => {
  const [destination, setDestination] = useState(value.destinationUrl || '');
  const [urlError, setUrlError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const created = !!value.shortUrl;

  const handleDestinationChange = (e) => {
    const url = e.target.value;
    setDestination(url);
    setUrlError(url ? validateUrl(url) : '');
    onChange({ ...value, destinationUrl: url });
  };

  const create = async () => {
    if (!destination || urlError) return;

    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/v1/rebrandly/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ destination_url: destination, title: title || destination })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create link');

      onChange({
        destinationUrl: destination,
        shortUrl: data.shortUrl,
        rebrandlyId: data.id,
        slashtag: data.slashtag,
        createdAt: data.createdAt
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!value.rebrandlyId) return;

    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/rebrandly/${value.rebrandlyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete link');
      }
      onChange({ destinationUrl: destination });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`https://${value.shortUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* буфер недоступен — не критично */
    }
  };

  return (
    <div>
      <label className='block text-xs font-medium text-slate-500 mb-1'>
        {label} <span className='text-slate-400'>(optional)</span>
      </label>
      <div className='flex gap-2'>
        <input
          type='url'
          value={destination}
          onChange={handleDestinationChange}
          disabled={created}
          placeholder='https://example.com'
          className={`flex-1 min-w-0 shadow-sm border rounded px-2 py-1.5 text-sm ${
            urlError ? 'border-red-400' : 'border-slate-300'
          } ${created ? 'bg-slate-50 text-slate-500' : ''}`}
        />
        <button
          type='button'
          onClick={create}
          disabled={!destination || !!urlError || busy || created}
          className={`shrink-0 px-3 py-1.5 text-sm rounded text-white ${
            !destination || !!urlError || busy || created
              ? 'bg-slate-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {busy ? 'Working…' : 'Create link'}
        </button>
      </div>

      {urlError && <p className='text-xs text-red-600 mt-1'>{urlError}</p>}
      {error && <p className='text-xs text-red-600 mt-1'>{error}</p>}
      {!urlError && !error && hint && <p className='text-xs text-slate-400 mt-1'>{hint}</p>}

      {created && (
        <div className='mt-2 flex items-center gap-2 text-sm'>
          <span className='text-slate-500'>Short link:</span>
          <span className='font-medium text-slate-900'>https://{value.shortUrl}</span>
          <button type='button' onClick={copy} className='text-xs text-blue-600 hover:text-blue-800'>
            {copied ? 'copied' : 'copy'}
          </button>
          <button
            type='button'
            onClick={remove}
            disabled={busy}
            className='text-xs text-red-600 hover:text-red-800 ml-auto'
          >
            Remove link
          </button>
        </div>
      )}
    </div>
  );
};

export default ShortLinkField;
